import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/utils/cn'

export interface TooltipPortalProps {
  children: React.ReactNode
  isVisible: boolean
  targetRef: React.RefObject<HTMLElement | null>
  className?: string
  placement?: 'top' | 'bottom' | 'left' | 'right'
  offset?: number
}

export const TooltipPortal: React.FC<TooltipPortalProps> = ({
  children,
  isVisible,
  targetRef,
  className,
  placement = 'top',
  offset = 8
}) => {
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isVisible || !targetRef.current) return

    const calculatePosition = () => {
      const targetRect = targetRef.current!.getBoundingClientRect()
      const tooltipRect = tooltipRef.current?.getBoundingClientRect()

      let top = 0
      let left = 0

      const tooltipWidth = tooltipRect?.width || 0
      const tooltipHeight = tooltipRect?.height || 0

      switch (placement) {
        case 'top':
          top = targetRect.top - tooltipHeight - offset
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
          break
        case 'bottom':
          top = targetRect.bottom + offset
          left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2
          break
        case 'left':
          top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
          left = targetRect.left - tooltipWidth - offset
          break
        case 'right':
          top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2
          left = targetRect.right + offset
          break
      }

      // Ensure tooltip stays within viewport
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const padding = 10

      // Adjust horizontal position
      if (left + tooltipWidth > viewportWidth - padding) {
        left = viewportWidth - tooltipWidth - padding
      }
      if (left < padding) {
        left = padding
      }

      // Adjust vertical position and flip if needed
      if (placement === 'top' && top < padding) {
        // Flip to bottom
        top = targetRect.bottom + offset
      } else if (placement === 'bottom' && top + tooltipHeight > viewportHeight - padding) {
        // Flip to top
        top = targetRect.top - tooltipHeight - offset
      }

      setPosition({ top, left })
    }

    // Initial calculation
    calculatePosition()

    // Use requestAnimationFrame for smoother updates
    const rafId = requestAnimationFrame(calculatePosition)

    // Recalculate on scroll or resize
    const handleScroll = () => calculatePosition()
    const handleResize = () => calculatePosition()

    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleResize)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [isVisible, targetRef, placement, offset])

  if (!isVisible) return null

  return createPortal(
    <div
      ref={tooltipRef}
      className={cn(
        'fixed z-tooltip pointer-events-none',
        'transition-opacity duration-200',
        isVisible ? 'opacity-100' : 'opacity-0',
        className
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`
      }}
    >
      {children}
    </div>,
    document.body
  )
}

export default TooltipPortal
